"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Icon } from "@to/ui";
import { AD_NAV_CTA, AD_NAV_TEXT_LINKS, withBasePath } from "./nav-links";
import { AgentAuthButton, PublicAgentSignInButton } from "./AgentAuthButton";
import { WeatherChip } from "./WeatherChip";

/**
 * Primary nav (v1 reference style) — desktop row in order: weather pill
 * (desktop-only chrome) → links → solid "Browse Directory" CTA pill → the Clerk
 * "Agent sign in" affordance, with a mobile hamburger menu (links + CTA + auth).
 * Active state matches the logical path (the dev org prefix is stripped). Every
 * color via `--brand-*`; icons via `Icon` (Material Symbols Rounded:
 * `menu` / `close`).
 */
export function Nav({
  basePath,
  clerkEnabled,
  publicSignIn = false,
}: {
  basePath: string;
  clerkEnabled: boolean;
  publicSignIn?: boolean;
}) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // Strip the dev org prefix (e.g. /star/agents → /agents) for active matching.
  const isDev = process.env.NODE_ENV === "development";
  const segments = pathname.split("/").filter(Boolean);
  const logicalPath =
    isDev && basePath && segments.length > 0
      ? "/" + segments.slice(1).join("/")
      : pathname;

  const isActive = (href: string) =>
    href === "/"
      ? logicalPath === "/" || logicalPath === ""
      : logicalPath === href || logicalPath.startsWith(`${href}/`);

  return (
    <div className="flex items-center gap-2 md:gap-6">
      {/* Weather pill — desktop-only chrome (saves mobile header space) */}
      <div className="hidden md:flex">
        <WeatherChip />
      </div>

      {/* Desktop nav — text links only; /agents renders as the CTA pill below */}
      <nav aria-label="Main" className="hidden items-center gap-8 md:flex">
        {AD_NAV_TEXT_LINKS.map((link) => {
          const active = isActive(link.href);
          return (
            <Link
              key={link.href}
              href={withBasePath(basePath, link.href)}
              prefetch={false}
              className="text-sm font-medium transition-colors"
              style={{
                color: active ? "var(--brand-primary-text, var(--brand-primary))" : "var(--brand-text)",
                borderBottom: `2px solid ${active ? "var(--brand-primary)" : "transparent"}`,
                paddingBottom: "2px",
              }}
            >
              {link.label}
            </Link>
          );
        })}
      </nav>

      {/* Find an Agent CTA — flat solid pill (v1: no shadow, no transform) */}
      <Link
        href={withBasePath(basePath, AD_NAV_CTA.href)}
        prefetch={false}
        className="hidden rounded-full px-4 py-2 text-sm font-medium transition-colors md:inline-flex"
        style={{
          backgroundColor: "var(--brand-button-bg, var(--brand-primary))",
          color: "var(--brand-primary-foreground)",
        }}
      >
        {AD_NAV_CTA.label}
      </Link>

      {(clerkEnabled || publicSignIn) && (
        <div className="hidden md:block">
          {publicSignIn ? <PublicAgentSignInButton /> : <AgentAuthButton />}
        </div>
      )}

      {/* Mobile hamburger */}
      <button
        type="button"
        className="md:hidden"
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        style={{ color: "var(--brand-text)" }}
      >
        <Icon name={open ? "close" : "menu"} size={24} />
      </button>

      {/* Mobile menu */}
      {open && (
        <div
          className="absolute inset-x-0 top-full z-50 border-t md:hidden"
          style={{
            backgroundColor: "var(--brand-background)",
            borderColor: "var(--brand-card-border)",
          }}
        >
          <div className="flex flex-col gap-1 px-4 py-4">
            {AD_NAV_TEXT_LINKS.map((link) => (
              <Link
                key={link.href}
                href={withBasePath(basePath, link.href)}
                prefetch={false}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium transition-colors"
                style={{ color: "var(--brand-text)" }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              href={withBasePath(basePath, AD_NAV_CTA.href)}
              prefetch={false}
              onClick={() => setOpen(false)}
              className="mx-3 mt-2 rounded-full px-4 py-2 text-center text-sm font-medium transition-colors"
              style={{
                backgroundColor: "var(--brand-button-bg, var(--brand-primary))",
                color: "var(--brand-primary-foreground)",
              }}
            >
              {AD_NAV_CTA.label}
            </Link>
            {(clerkEnabled || publicSignIn) && (
              <div className="mt-2 px-3">
                {publicSignIn ? <PublicAgentSignInButton /> : <AgentAuthButton />}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
